import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch } from 'react-redux'
import _ from 'lodash'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import { styled, useTheme } from '@mui/material/styles'
import MuiCard from '@mui/material/Card'
import InputAdornment from '@mui/material/InputAdornment'
import FormHelperText from '@mui/material/FormHelperText'
import Avatar from '@mui/material/Avatar'

import EyeOutline from 'mdi-material-ui/EyeOutline'
import EyeOffOutline from 'mdi-material-ui/EyeOffOutline'

import BlankLayout from '@core/layouts/BlankLayout'
import themeConfig from 'configs/themeConfig'
import FooterIllustrations from 'views/pages/auth/FooterIllustrations'
import APICall from 'network/APICall'
import EndPoints from 'network/Endpoints'
import { loginUserData, permission } from 'store/reducer/AuthReducer'
import Storage from '@core/helper/Storage'
import LogoImage from 'assets/images/logo/ksijLogo.svg'
import Loader from '@core/helper/Loader'

const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: 450 }
}))

const schema = yup.object().shape({
  userName: yup.string().required('Username is a required field'),
  password: yup.string().required('Password is a required field')
})

const Login = () => {
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const theme = useTheme()

  const defaultValues = {
    password: '',
    userName: ''
  }

  const {
    reset,
    control,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const onSubmit = data => {
    const { userName, password } = data
    if (!userName || !password) {
      setError('userName', {
        type: 'manual',
        message: 'Username or Password is invalid'
      })
      return
    }

    Loader.isLoading(true);

    const payload = {
      userName,
      password
    }

    APICall('post', payload, EndPoints.adminLogin)
      .then(async response => {
        const { isError, data } = response.data
        global.userData = data
        if (isError && response.statusCode === 400) {
         
          setError('userName', {
            type: 'manual',
            message: 'Email or Password is invalid'
          })
          return
        }

        if (!isError && response.status === 200) {
          const obj2 = {
            dashboard: {
              view: true,
              edit: true,
              delete: true
            },
            ...data.user.config
          }

          data.user.config = obj2
          localStorage.setItem('@userData', JSON.stringify(data))
          Storage.setUserData({ data })
          dispatch(loginUserData({ data }))
          dispatch(permission())
          reset(defaultValues)
          Loader.isLoading(false);
          await Storage.getUserData()
          if (data) {
            const dataset1 = JSON.parse(JSON.stringify(data))
            const datavalue = dataset1?.user

            const pushdata = []

            Object.entries(datavalue?.config).map(([key, value]) => {
              if (!value.view && !value.edit && !value.delete) {
              } else {
                pushdata.push(key)
              }
              return null
            })
            if (datavalue.role === '2') {
              const split = _.split(pushdata[0], '_').join('')
              if (split === 'program') {
                navigate('/program/today')
              } else if (split === 'library') {
                navigate('/library/category')
              } else if (split === 'business') {
                navigate('/business/category')
              } else if (split === 'calendersetting') {
                navigate('/calendersetting/hijricalender')
              } else if (split === 'donation') {
                navigate('/donation/donation')
              } else {
                navigate(`/${split}`)
              }
            } else {
              navigate('/dashboard')
            }
          }
        }
      })
      .catch(error => {
        Loader.isLoading(false);
        return error
      })
      .finally(() => {
        Loader.isLoading(false);
      })
  }

  return (
    <BlankLayout>
      <Box className='content-center'>
        <Card
          sx={{
            zIndex: 1,
            boxShadow: 6,
            width: '100%',
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          <CardContent
            sx={{ p: theme => `${theme.spacing(6, 4, 6.5)} !important` }}
          >
            <Box
              sx={{
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Avatar
                alt='Img'
                sx={{ width: 60, height: 60 }}
                src={LogoImage}
              />
              <Typography
                variant='h6'
                sx={{
                  ml: 1.5,
                  lineHeight: 1,
                  fontWeight: 700,
                  fontSize: '1.4rem !important',
                  color: 'text.secondary'
                }}
              >
                {themeConfig.templateName}
              </Typography>
            </Box>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant='h5'
                sx={{
                  mb: 0.5,
                  fontWeight: 500,
                  letterSpacing: '0.18px',
                  fontSize: 22,
                  color: 'text.secondary'
                }}
              >
                {`Welcome to ${themeConfig.templateName}! 👋🏻`}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Please sign-in to your account and start the adventure
              </Typography>
            </Box>
            <form
              noValidate
              autoComplete='off'
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormControl
                fullWidth
                sx={{
                  mb: 4,
                  borderRadius: 10
                }}
              >
                <Controller
                  name='userName'
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      autoFocus
                      label='Username'
                      type='email'
                      value={value}
                      onChange={onChange}
                      error={Boolean(errors.userName)}
                      placeholder='admin@materialize.com'
                      aria-describedby='validation-basic-userName'
                    />
                  )}
                />
                {errors.userName && (
                  <FormHelperText
                    sx={{ color: 'error.main' }}
                    id='validation-basic-userName'
                  >
                    {errors.userName.message}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl fullWidth>
                <InputLabel
                  htmlFor='auth-login-v2-password'
                  error={Boolean(errors.password)}
                >
                  Password
                </InputLabel>
                <Controller
                  name='password'
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <OutlinedInput
                      value={value}
                      label='Password'
                      onChange={onChange}
                      id='auth-login-v2-password'
                      error={Boolean(errors.password)}
                      type={showPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            edge='end'
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOutline /> : <EyeOffOutline />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  )}
                />
                {errors.password && (
                  <FormHelperText sx={{ color: 'error.main' }} id=''>
                    {errors.password.message}
                  </FormHelperText>
                )}
              </FormControl>

              <Grid item xs={12} sx={{ mt: 5 }}>
                <Button
                  fullWidth
                  size='large'
                  type='submit'
                  variant='contained'
                  sx={{
                    mb: 7,
                    backgroundColor: theme.palette.primary.main
                  }}
                >
                  Login
                </Button>
              </Grid>
            </form>
          </CardContent>
        </Card>
        <FooterIllustrations />
      </Box>
    </BlankLayout>
  )
}

export default Login
